import classNames from 'classnames';
import { useState, useEffect, useRef } from 'react';
import styles from './Picture.module.scss';
import { GetImageUrl, GetSrcSetString } from 'helpers/getImageUrl';
import config from 'helpers/imageSizes';
import { inView } from 'helpers/inView';

export interface PictureProps extends Umbraco.Image {
	sizes?: string;
	lazyload?: boolean;
	objectFitFallback?: boolean;
	isCover?: boolean;
	aspectRatio?: number;
	className?: string;
	width?: number;
	fallback?: React.ReactNode;
	onFallback?: (isFallback: boolean) => void;
}

export const Picture: React.FC<PictureProps> = ({
	isCover = true,
	sizes = '100vw',
	url,
	lazyload = true,
	objectFitFallback = true,
	properties,
	focalPoint,
	aspectRatio,
	className,
	width,
	fallback,
	onFallback,
}) => {
	const { thumbnailSize, fallbackSize } = config.images;

	const thumbnailImageUrl = GetImageUrl({ url, width: thumbnailSize });
	const thumbnailImageUrlWebp = GetImageUrl({
		url,
		width: thumbnailSize,
		format: 'webp',
	});

	const [src, setSrc] = useState(thumbnailImageUrl);
	const [imgSrcSet, setImgSrcSet] = useState(thumbnailImageUrl);
	const [sourceSrcSet, setSourceSrcSet] = useState(thumbnailImageUrlWebp);
	const [isFallback, setIsFallback] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [prevUrl, setPrevUrl] = useState(url);

	if (url !== prevUrl) {
		setPrevUrl(url);
		setHasError(false);
		setSrc(thumbnailImageUrl);
		setImgSrcSet(thumbnailImageUrl);
		setSourceSrcSet(thumbnailImageUrlWebp);
	}

	const pictureRef = useRef(null);
	const imgRef = useRef<HTMLImageElement>(null);

	const showFallback = Boolean(fallback) && (!url || hasError);

	useEffect(() => {
		// The image can fail before hydration attaches onError (SSR), so check its load state on mount
		if (imgRef.current?.complete && imgRef.current.naturalWidth === 0) setHasError(true);
	}, []);

	useEffect(() => {
		onFallback?.(showFallback);
	}, [showFallback]);

	useEffect(() => {
		function reveal() {
			const srcSetString = GetSrcSetString({
				url,
				focalPoint,
				aspectRatio,
			});
			const srcSetStringWebp = GetSrcSetString({
				url,
				focalPoint,
				format: 'webp',
				aspectRatio,
			});
			const srcString = GetImageUrl({ url, width: fallbackSize });

			setImgSrcSet(srcSetString);
			setSourceSrcSet(srcSetStringWebp);
			setSrc(srcString);
		}

		function objectFit() {
			// If the useragent doesn't support ObjectFit (IE), we need to
			// add fallback image to the picture element
			const supportObjectFit = 'objectFit' in document.documentElement.style;

			if (!supportObjectFit) setIsFallback(true);
		}

		if (url) {
			if (objectFitFallback) objectFit();
			if (!lazyload) reveal();
			if (lazyload) return inView({ elm: pictureRef.current, callback: reveal });
		}
	}, [url, lazyload, objectFitFallback, aspectRatio, fallbackSize, focalPoint]);

	if (showFallback) {
		return <span className={classNames(styles.Picture_fallback, className)}>{fallback}</span>;
	}

	return (
		<picture
			ref={pictureRef}
			className={classNames(styles.Picture, { [styles.Picture___cover]: isCover }, { [styles.isFallback]: isFallback }, className)}
			style={isFallback ? { backgroundImage: `url(${src})` } : {}}
		>
			{!isFallback && (
				<>
					{sourceSrcSet && <source sizes={sizes} type="image/webp" srcSet={`${sourceSrcSet ? sourceSrcSet : null} 100w`} />}

					{(src || imgSrcSet) && (
						<img
							ref={imgRef}
							onError={() => setHasError(true)}
							width={width ? width : 100}
							src={src ? src : '#'}
							alt={properties?.altText ? properties?.altText : 'image'}
							className={styles.Picture_asset}
							srcSet={`${imgSrcSet ? imgSrcSet : null} 100w`}
							sizes={sizes}
						/>
					)}
				</>
			)}
		</picture>
	);
};
